<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">{{ title }}</h5>
    </div>
    <div class="offcanvas-body">
      <div class="download-dates h-100" v-show="!loadingDownload && !requestAccepted">
        <div v-if="!checkingAvailability && !exportAvailable" class="alert alert-danger" role="alert" data-test="textExportUnavailable">
          {{ availabilityCheckFailed ? $t('request_error') : $t('exam.bulkExportAlreadyRunning') }}
        </div>

        <!-- Start Date Picker -->
        <div class="form-floating">
          <date-picker
            v-model="startDate"
            :locale="locale"
            value-type="date"
            :selectText="$t('exam.select')"
            :cancelText="$t('cancel')"
            :format="$t('dateFormatFns')"
            :inputFormat="$t('dateFormatFns')"
            :enableTimePicker="false"
            :disabled-dates="disabledDate"
            data-test="datePickerStart"/>
          <label>{{ $t('general.startDate') }}</label>
        </div>

        <!-- End Date Picker -->
        <div class="form-floating">
          <date-picker
            v-model="endDate"
            :locale="locale"
            value-type="date"
            :selectText="$t('exam.select')"
            :cancelText="$t('cancel')"
            :format="$t('dateFormatFns')"
            :inputFormat="$t('dateFormatFns')"
            :enableTimePicker="false"
            :disabled-dates="disabledDate"
            data-test="datePickerEnd"/>
          <label>{{ $t('general.endDate') }}</label>
        </div>

        <!-- CC e-mails -->
        <div class="cc-emails">
          <div class="form-floating v-select-floating">
            <v-select
              label="label"
              class="v-select form"
              id="ccEmails"
              :placeholder="$t('exam.ccClinicUsers')"
              v-model="ccEmailSelected"
              :reduce="x => x.value"
              :options="availableClinicUserOptions"
              :loading="loadingClinicUsers"
              @option:selected="addCcEmail"
              data-test="inputCcEmails"/>
            <label for="ccEmails">{{ $t('exam.ccClinicUsers') }}</label>
          </div>

          <div class="cc-selected-list" v-if="ccEmails.length">
            <span class="badge d-flex align-items-center py-1 rounded-pill" v-for="email in ccEmails" :key="email">
              {{ email }}
              <button type="button" class="btn-close btn-close-white ms-2" @click="removeCcEmail(email)" data-test="btnRemoveCcEmail"></button>
            </span>
          </div>

          <small class="form-hint">{{ $t('exam.ccClinicUsersHint') }}</small>
        </div>

        <div class="offcanvas-footer">
          <button type="button" class="btn" :class="okButtonClass" data-test="btnConfirm" :disabled="checkingAvailability || !exportAvailable || loadingDownload" @click="doConfirm">
            <span v-if="checkingAvailability" class="spinner-border spinner-border-sm me-2"></span>
            {{ okButton }}
          </button>
          <button type="button" class="btn" :class="cancelButtonClass" data-bs-dismiss="offcanvas" v-show="cancelButton" data-test="btnCancel" @click="doCancel">{{ cancelButton }}</button>
        </div>

        <!-- Export history -->
        <BaseExportHistory ref="exportHistory" batch-type="backup"/>
      </div>

      <div class="text-center" v-show="loadingDownload && !requestAccepted">
        <h3>{{ $t('exam.preparingFileForDownload') }}</h3>
        <img class="mb-3" src="@/assets/images/loading_points.gif" alt=""/>
        <div v-if="downloadInfo.examsCount">
          <p><strong>{{ $t('exam.examsCount') }}</strong> {{ downloadInfo.examsCount }}</p>
          <p><strong>{{ $t('exam.imagesCount') }}</strong> {{ downloadInfo.imageCount }}</p>
          <p><strong>{{ $t('exam.estimatedSize') }}</strong> {{ downloadInfo.payloadStimatedSize }} Mb</p>
        </div>
      </div>

      <div class="text-center" v-show="!loadingDownload && requestAccepted">
        <h3>{{ $t('exam.bulkExportRequested') }}</h3>
        <p>{{ $t('exam.bulkExportEmailSoon') }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-title, h3 {
    color:                              var(--Brand-Primary, #1B294A);
    font-size:                          38px;
    font-weight:                        300;
    line-height:                        normal;
    margin:                             26px 0;
  }

  h3 {
    font-size:                          var(--font-size-18);
  }

  .offcanvas-body {
    p, li {
      color:                            var(--Brand-Primary, #1B294A);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%;
      margin-bottom:                    26px;
    }

    .download-dates {
      display:                          flex;
      flex-direction:                   column;
      gap:                              19px;

      .form-floating label {
        padding-top:                    0.4rem;
      }

      .cc-emails {
        // _offcanvas.scss drops the menu's max-height inside an offcanvas, so it grows
        // unbounded and flips over the field: pin it below and cap it to scroll instead.
        :deep(ul.vs__dropdown-menu) {
          top:                          100% !important;
          bottom:                       unset !important;
          max-height:                   320px !important;
        }

        :deep(.vs__selected-options) {
          padding-left:                 6px;
        }

        :deep(.v-select-floating label) {
          transform:                    none;
        }

        :deep(.vs__search),
        :deep(.vs__selected) {
          margin-top:                   -5px;
        }

        .cc-selected-list {
          display:                      flex;
          flex-wrap:                    wrap;
          gap:                          6px;
          margin-top:                   10px;

          .badge {
            background:                 var(--Brand-Secondary);
            color:                      #fff;

            button {
              font-size:                0.5rem;
            }
          }
        }

        .form-hint {
          display:                      block;
          margin-top:                   6px;
          color:                        var(--Shades-50);
          font-size:                    var(--font-size-12);
          font-weight:                  300;
          line-height:                  140%;
        }
      }
    }
  }

  .btn {
    font-size:                          var(--font-size-14);
    font-weight:                        700;
    letter-spacing:                     4.2px;
    text-align:                         center;
    text-transform:                     uppercase;
    width:                              100%;
    padding:                            20px;

    &.btn-outline-secondary {
      padding:                          20px 18px;
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

// user/list pages 20 at a time, so this caps the CC list at 200 clinic users.
const MAX_USER_PAGES = 10

// Roles that must not show up in the CC list: the owner already receives the download link, and the technician
// and the specialist are not meant to be copied on it. The backend leaves them out (a user is matched by its highest role).
const CC_EXCLUDED_ROLES = ['owner', 'technician', 'specialist']

export default {
  name: 'ExamBackupImagesOffcanvas',
  props: {
    offcanvas_id: { type: [String, Number] },
  },
  data: () => ({
    alert: {
      message: null,
      class: null
    },
    title: null,
    text: null,
    okButton: null,
    okButtonClass: null,
    cancelButton: null,
    cancelButtonClass: null,
    offcanvas_download: null,
    clinicUUID: null,
    loadingDownload: false,
    requestAccepted: false,
    checkingAvailability: false,
    exportAvailable: true,
    availabilityCheckFailed: false,
    downloadInfo: {
      examsCount: null,
      imageCount: null,
      payloadStimatedSize: null
    },
    startDate: new Date(),
    endDate: new Date(),
    ccEmails: [],
    ccEmailSelected: null,
    clinicUserOptions: [],
    loadingClinicUsers: false,
    session: {
      collaborator: {},
      currentClinic: {},
      features: []
    },
  }),
  computed: {
    locale() {
      return this.$utils.datePickerLocale()
    },
    availableClinicUserOptions() {
      return this.clinicUserOptions.filter(option => !this.ccEmails.includes(option.value))
    }
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
  },
  methods: {
    async fetchClinicUserEmails() {
      this.loadingClinicUsers = true

      try {
        const users = []
        let page = 1
        let totalCount = Infinity

        while (users.length < totalCount && page <= MAX_USER_PAGES) {
          const response = await this.$userService.list({ page, excludeRoles: CC_EXCLUDED_ROLES })
          const pageUsers = response.result || []
          if (!pageUsers.length) break

          users.push(...pageUsers)
          totalCount = response.totalCount ?? users.length
          page++
        }

        this.clinicUserOptions = users
          .filter(u => u.email)
          .map(u => ({ label: `${u.collaborator?.rawName} — ${u.email}`, value: u.email }))
      } catch (err) {
        this.clinicUserOptions = []
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        this.loadingClinicUsers = false
      }
    },

    // The picked e-mail is moved to the list below, leaving the select empty for the next pick.
    addCcEmail(option) {
      const email = option?.value ?? option
      if (email && !this.ccEmails.includes(email)) this.ccEmails.push(email)

      this.$nextTick(() => { this.ccEmailSelected = null })
    },

    removeCcEmail(email) {
      this.ccEmails = this.ccEmails.filter(e => e !== email)
    },

    clearForm() {
      this.loadingDownload = false
      this.requestAccepted = false
      this.startDate = new Date()
      this.endDate = new Date()
      this.ccEmails = []
      this.ccEmailSelected = null
    },

    show(options = {}) {
      this.clearForm()
      this.fetchClinicUserEmails()

      if (options.title) this.title = options.title
      if (options.text) this.text = options.text
      if (options.clinicUUID) this.clinicUUID = options.clinicUUID
      if (options.okButton) {
        this.okButton = options.okButton
        this.okButtonClass = options.okButtonClass
      }
      if (options.cancelButton) {
        this.cancelButton = options.cancelButton
        this.cancelButtonClass = options.cancelButtonClass
      }

      this.offcanvas_download = new bootstrap.Offcanvas(document.getElementById(this.offcanvas_id))
      this.offcanvas_download.show();
      this.checkAvailability()
      this.$refs.exportHistory?.fetchHistory()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    async checkAvailability() {
      this.checkingAvailability = true
      this.exportAvailable = false
      this.availabilityCheckFailed = false

      try {
        let check = await this.$clinicService.canStartExport({ batchType: 'backup' })
        this.exportAvailable = !!check.canStart
      } catch (err) {
        this.exportAvailable = false
        this.availabilityCheckFailed = true
      } finally {
        this.checkingAvailability = false
      }
    },

    doConfirm() {
      if (this.loadingDownload || this.requestAccepted) return

      // startDate === endDate is valid (exports the entire day) — the backend normalizes it
      if (this.startDate && this.endDate && new Date(this.startDate) > new Date(this.endDate)) {
        this.$message.popup(this, this.$t('exam.startDateAfterEndDate'), 'operationFail', 'error')
        return
      }

      if (this.endDate && new Date(this.endDate) > new Date()) {
        this.$message.popup(this, this.$t('exam.endDateInFuture'), 'operationFail', 'error')
        return
      }

      this.doDownload(this.ccEmails)
      this.resolvePromise(true)
    },

    doCancel() {
      this.loadingDownload = false
      this.requestAccepted = false
      this.ccEmails = []
      this.ccEmailSelected = null
      this.offcanvas_download = null;
      this.resolvePromise(false)
    },

    disabledDate(date) {
      const today = new Date()
      return date > today
    },

    async doDownload(ccEmails = []) {
      let params = {
        clinic: this.session.currentClinic,
        collaborator: this.session.collaborator,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
        ccEmails,
      };
      this.loadingDownload = true
      NProgress.start()

      try{
        let response = await this.$examService.backupImagesFromExams(params);
        this.requestAccepted = response?.status === 200 || response?.status === 202;
        if (!this.requestAccepted) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error')
        }
      } catch(err){
        // The backend answers 400 when the clinic has no e-mail to receive the download link (the dates are
        // already validated here); the other server messages are shown only when they have a translation.
        const errorMessage = err.response?.data?.errorMessage
        let message = 'request_error'
        if (err.response?.status === 400)
          message = 'exam.clinicHasNoEmail'
        else if (errorMessage && this.$i18n.te(errorMessage))
          message = errorMessage

        this.$message.popup(this, message, 'operationFail', 'error')
      } finally {
        if (!this.requestAccepted) {
          await this.checkAvailability()
        }
        this.loadingDownload = false;
        NProgress.done(true)
      }
    }
  }
}
</script>